<template>
  <div class="relative w-full">
    <ui-menu
      ref="menuRef"
      class="w-full"
      size="match"
      match-width
      :disabled="isDisabled"
      max-width="100%"
      :max-height="menuMaxHeight"
      @visibility-change="handleVisibilityChange"
    >
      <ui-menu-select-trigger
        ref="triggerRef"
        data-testid="multilevel-select-trigger"
        :active="isOpen"
        :disabled="isDisabled"
        :placeholder="!selectedLabelChild"
        @keydown:down.stop.prevent="focusOptionFromTrigger('first')"
        @keydown:up.stop.prevent="focusOptionFromTrigger('last')"
        @keydown:esc.stop.prevent="handleEscape"
      >
        <div class="flex min-w-0 flex-1 items-center gap-1">
          <div class="w-0 flex-1 overflow-hidden text-left">
            <span v-if="selectedLabelChild" class="flex items-center gap-1">
              <span class="min-w-0 max-w-[50%] truncate">{{ selectedLabelParent }}</span>
              <span v-if="selectedLabelParent" class="shrink-0">&gt;</span>
              <span class="min-w-0 flex-1 truncate">{{ selectedLabelChild }}</span>
            </span>
            <span v-else>
              {{ placeholder }}
            </span>
          </div>
          <button
            v-if="hasValue"
            data-testid="multilevel-select-clear"
            type="button"
            class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-none bg-transparent p-0 text-gray-60 transition-all duration-150 ease-in hover:bg-gray-10 hover:text-gray-80"
            :aria-label="props.clearLabel"
            @click.stop.prevent="clearSelection"
          >
            <x-icon class="h-full w-full" />
          </button>
        </div>
      </ui-menu-select-trigger>
      <template #list>
        <ui-multi-level-select-list
          ref="listRef"
          :model-value="modelValue"
          :options="options"
          :root-label="rootLabel"
          :empty-label="emptyLabel"
          :max-height="menuMaxHeight"
          @update:model-value="$emit('update:modelValue', $event)"
          @select="handleOptionSelect"
          @escape="handleEscape"
          @navigation-change="handleListNavigationChange"
        />
      </template>
      <template #after>
        <slot name="after" :is-nested="isListNested" />
      </template>
    </ui-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, useSlots } from 'vue'
import { XIcon } from '@klausapp/ui-kit/icons'
import type { MultiLevelOption } from '../types'
import UiMenu from './UiMenu/UiMenu.vue'
import UiMenuSelectTrigger from './UiMenu/triggers/UiMenuSelectTrigger.vue'
import UiMultiLevelSelectList from './UiMultiLevelSelectList.vue'
import { findMultiLevelOptionPath } from './UiMultiLevelSelect.utils'

interface Props {
  modelValue?: string | null
  options: MultiLevelOption[]
  selectedLabel?: string | null
  placeholder?: string
  rootLabel?: string
  clearLabel?: string
  emptyLabel?: string
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'navigation-change', value: { depth: number; isNested: boolean }): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  selectedLabel: null,
  placeholder: '',
  rootLabel: '',
  clearLabel: '',
  emptyLabel: '',
})

const emit = defineEmits<Emits>()
const slots = useSlots()

const isOpen = ref(false)
const triggerRef = ref<InstanceType<typeof UiMenuSelectTrigger> | null>(null)
const listRef = ref<InstanceType<typeof UiMultiLevelSelectList> | null>(null)
const menuRef = ref<InstanceType<typeof UiMenu> | null>(null)
const isListNested = ref(false)
const menuMaxHeight = 364
const optionToFocusOnOpen = ref<'first' | 'last' | null>(null)

const selectedPath = computed<MultiLevelOption[]>(() => findMultiLevelOptionPath(props.options, props.modelValue))

const selectedLabelParent = computed(() => (selectedPath.value.length > 1 ? selectedPath.value[0].name : null))

const selectedLabelChild = computed(() => selectedPath.value.at(-1)?.name ?? props.selectedLabel ?? null)

const hasValue = computed(() => !!props.modelValue)
const hasAfterSlot = computed(() => !!slots.after)
const isDisabled = computed(() => props.options.length === 0 && !hasAfterSlot.value)

const handleOptionSelect = async () => {
  await closeDropdown({ restoreFocus: true })
}

const focusOption = (option: 'first' | 'last') => {
  if (option === 'first') {
    listRef.value?.focusFirstOption()
    return
  }

  listRef.value?.focusLastOption()
}

const focusOptionFromTrigger = async (option: 'first' | 'last') => {
  if (!isOpen.value) {
    optionToFocusOnOpen.value = option
    menuRef.value?.show()
    return
  }

  await nextTick()
  focusOption(option)
}

const focusTrigger = () => {
  const el = triggerRef.value?.$el
  // $el is the button element itself, not a wrapper
  if (el instanceof HTMLButtonElement) {
    el.focus()
  }
}

const clearSelection = async () => {
  emit('update:modelValue', null)

  if (isOpen.value) await closeDropdown({ restoreFocus: true })
}

const handleEscape = () => closeDropdown({ restoreFocus: true })

const handleListNavigationChange = (value: { depth: number; isNested: boolean }) => {
  isListNested.value = value.isNested
  emit('navigation-change', value)
}

const closeDropdown = async ({ restoreFocus = false }: { restoreFocus?: boolean } = {}) => {
  if (!isOpen.value) return

  menuRef.value?.hide()

  if (restoreFocus) {
    await nextTick()
    focusTrigger()
  }
}

const handleVisibilityChange = async (open: boolean) => {
  isOpen.value = open

  if (open) {
    await nextTick()
    if (optionToFocusOnOpen.value) {
      focusOption(optionToFocusOnOpen.value)
      optionToFocusOnOpen.value = null
    }
    return
  }

  optionToFocusOnOpen.value = null
  listRef.value?.reset()
}

defineExpose({
  close: () => closeDropdown({ restoreFocus: true }),
})
</script>
